import { message } from 'ant-design-vue'
import request, { IPage, IWorkspaceResponse, IListWorkspaceResponse } from '/@/api/http/request'
import { TaskType, TaskStatus, OutOfControlAction } from '/@/types/task'
import { WaylineType } from '/@/types/wayline'

const HTTP_PREFIX = '/wayline/api/v1'

// Get Wayline Files
export const getWaylineFiles = async function (wid: string, body: {}): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${wid}/waylines?order_by=${body.order_by}&page=${body.page}&page_size=${body.page_size}`
  const result = await request.get(url)
  return result.data
}

// Download Wayline File. Ide preko lds-notify proxy-ja (server prati /url 302 -> MinIO i
// vrati KMZ preko HTTPS-a) da editor može da RE-OTVORI sačuvanu rutu; direktan /url daje
// http redirect koji HTTPS stranica blokira (mixed-content).
export const downloadWaylineFile = async function (workspaceId: string, waylineId: string): Promise<any> {
  const url = `/lds-notify/api/v1/wayline-kmz?ws=${workspaceId}&id=${waylineId}`
  const result = await request.get(url, { responseType: 'blob' })
  if (result.data.type === 'application/json') {
    const reader = new FileReader()
    reader.onload = function (e) {
      const text = reader.result as string
      const result = JSON.parse(text)
      message.error(result.message)
    }
    reader.readAsText(result.data, 'utf-8')
  } else {
    return result.data
  }
}

// Delete Wayline File
export const deleteWaylineFile = async function (workspaceId: string, waylineId: string): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/${waylineId}`
  const result = await request.delete(url)
  return result.data
}

export interface CreatePlan {
  name: string,
  file_id: string,
  dock_sn: string,
  task_type: TaskType, // task type
  wayline_type: WaylineType, // wayline type
  task_days: number[] // task execution dates (seconds)
  task_periods: number[][] // task execution times (seconds)
  rth_altitude: number // return-to-home altitude relative to the dock, 20 - 500
  out_of_control_action: OutOfControlAction // lost-control action
  min_battery_capacity?: number, // The minimum battery capacity of aircraft.
  min_storage_capacity?: number, // The minimum storage capacity of dock and aircraft.
}

// Create Wayline Job
export const createPlan = async function (workspaceId: string, plan: CreatePlan): Promise<IWorkspaceResponse<any>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/flight-tasks`
  const result = await request.post(url, plan)
  return result.data
}

export interface Task {
  job_id: string,
  job_name: string,
  task_type: TaskType, // task type
  file_id: string, // wayline file id
  file_name: string, // wayline name
  wayline_type: WaylineType, // wayline type
  dock_sn: string,
  dock_name: string,
  workspace_id: string,
  username: string,
  begin_time: string,
  end_time: string,
  execute_time: string,
  completed_time: string,
  status: TaskStatus, // task status
  progress: number, // execution progress
  code: number, // error code
  rth_altitude: number // return-to-home altitude relative to the dock, 20 - 500
  out_of_control_action: OutOfControlAction // lost-control action
  media_count: number // media count
  uploading:boolean // whether media is being uploaded
  uploaded_count: number // uploaded media count
}

// Get Wayline Jobs
export const getWaylineJobs = async function (workspaceId: string, page: IPage): Promise<IListWorkspaceResponse<Task>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs?page=${page.page}&page_size=${page.page_size}`
  const result = await request.get(url)
  return result.data
}

export interface DeleteTaskParams {
  job_id: string
}

//  Delete a dock task
export async function deleteTask (workspaceId: string, params: DeleteTaskParams): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs`
  const result = await request.delete(url, {
    params: params
  })
  return result.data
}

export enum UpdateTaskStatus {
  Suspend = 0, // suspend
  Resume = 1, // resume
}
export interface UpdateTaskStatusBody {
  job_id: string
  status: UpdateTaskStatus
}

// Update dock task status
export async function updateTaskStatus (workspaceId: string, body: UpdateTaskStatusBody): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${body.job_id}`
  const result = await request.put(url, {
    status: body.status
  })
  return result.data
}

// Upload Wayline file. Opciono takeoff koordinate -> serverska agl-guard brana ih koristi
// za tačnu AGL proveru (inače koristi prvu tačku rute).
export const importKmzFile = async function (workspaceId: string, file: {}, takeoff?: { lat: number, lng: number }): Promise<IWorkspaceResponse<any>> {
  let url = `${HTTP_PREFIX}/workspaces/${workspaceId}/waylines/file/upload`
  if (takeoff && Number.isFinite(takeoff.lat) && Number.isFinite(takeoff.lng)) {
    url += `?takeoffLat=${takeoff.lat}&takeoffLng=${takeoff.lng}`
  }
  const result = await request.post(url, file, {
    headers: {
      'Content-Type': 'multipart/form-data',
    }
  })
  return result.data
}

// Upload media immediately
export const uploadMediaFileNow = async function (workspaceId: string, jobId: string): Promise<IWorkspaceResponse<{}>> {
  const url = `${HTTP_PREFIX}/workspaces/${workspaceId}/jobs/${jobId}/media-highest`
  const result = await request.post(url)
  return result.data
}
